"use client";

import { useCallback, useMemo } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { getWebConfig } from "@/actions/config.action";
import type {
  WebConfigBadge,
  WebConfigMenu,
  WebConfigMenuKey,
  WebConfigResponse,
} from "@/types/core";
import type { MainServiceItemData } from "@/components/home/hero/mainServiceItems";

/** ******************************
 * Web config query keys
 ****************************** */
const webConfigQueryKeys = {
  root: () => ["web-config", "2.0.0", "ios"] as const,
};

/** ******************************
 * Web Config Badges
 ****************************** */
const useWebConfigBadges = () => {
  // Cache the non-critical badge configuration to avoid repeated homepage calls.
  return useQuery<WebConfigResponse, Error, WebConfigBadge[]>({
    queryKey: webConfigQueryKeys.root(),
    queryFn: getWebConfig,
    staleTime: 1000 * 60 * 5,
    select: (response) =>
      Array.isArray(response.data?.badges) ? response.data.badges : [],
  });
};

/** ******************************
 * Web Config Menu
 ****************************** */
function useWebConfigMenu(
  menuKey: WebConfigMenuKey,
): UseQueryResult<WebConfigMenu | undefined, Error>;
function useWebConfigMenu(): UseQueryResult<
  Partial<Record<WebConfigMenuKey, WebConfigMenu>>,
  Error
>;
function useWebConfigMenu(menuKey?: WebConfigMenuKey) {
  return useQuery({
    queryKey: webConfigQueryKeys.root(),
    queryFn: getWebConfig,
    staleTime: 1000 * 60 * 5,
    select: (response: WebConfigResponse) =>
      menuKey ? response.data?.menus?.[menuKey] : (response.data?.menus ?? {}),
  });
}

/** ******************************
 * Service Coming Soon
 ****************************** */
const useServiceComingSoon = () => {
  const { data: badgeData = [], isError: isBadgeConfigError } =
    useWebConfigBadges();
  const { data: menus = {} } = useWebConfigMenu();

  // Hide failed configuration and index valid badges by backend service key.
  const badgeByServiceKey = useMemo(
    () =>
      new Map(
        (isBadgeConfigError ? [] : badgeData).map(
          (badge) => [badge.key, badge.value] as const,
        ),
      ),
    [badgeData, isBadgeConfigError],
  );

  // Check if a service is coming soon dynamically from the API response (menus[key]?.enabled === false)
  // or if backend badge is marked "Coming soon".
  const isServiceComingSoon = useCallback(
    (item: MainServiceItemData | { remoteConfigKey?: string }) => {
      if (!item.remoteConfigKey) return false;

      // 1. Check if backend badge is marked "Coming soon"
      const badgeValue = badgeByServiceKey.get(item.remoteConfigKey);
      if (badgeValue?.trim().toLowerCase() === "coming soon") {
        return true;
      }

      // 2. Check if feature toggle is explicitly disabled
      const menu = menus[item.remoteConfigKey as WebConfigMenuKey];
      return menu ? menu.enabled === false : false;
    },
    [badgeByServiceKey, menus],
  );

  return {
    badgeByServiceKey,
    isServiceComingSoon,
    menus,
  };
};

export {
  webConfigQueryKeys,
  useWebConfigBadges,
  useWebConfigMenu,
  useServiceComingSoon,
};
